import React, { useRef, useState, useEffect } from 'react';
import { Box } from '@mui/material';

const OTP_LENGTH = 8;

const otpInputStyle: React.CSSProperties = {
  width: 40,
  height: 64,
  border: '1px solid #e7eaee',
  borderRadius: 8,
  textAlign: 'center',
  fontSize: 24,
  lineHeight: '28px',
  outline: 'none'
};

interface OtpVerificationFieldProps {
  onCompletionChanged?: (code: string, isComplete: boolean) => void;
}

interface OtpCharEntry {
  id: string;
  char: string;
}

const createInitialOtpState = (): OtpCharEntry[] =>
  new Array(OTP_LENGTH).fill(null).map((_, index) => ({
    id: `otp-char-${index + 1}`,
    char: ''
  }));

export const OtpVerificationField: React.FC<OtpVerificationFieldProps> = ({
  onCompletionChanged
}) => {
  const [codeChars, setCodeChars] = useState<OtpCharEntry[]>(
    createInitialOtpState
  );

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const cleanupOtpCode = (value: string) => {
    return value.replace(/[^a-zA-Z0-9]/g, '');
  };

  const handleChange = (index: number, value: string) => {
    if (!value) {
      const nextChars = [...codeChars];
      nextChars[index] = { ...nextChars[index], char: '' };
      setCodeChars(nextChars);
      return;
    }

    const cleaned = cleanupOtpCode(value);
    if (!cleaned) {
      return;
    }

    if (cleaned.length > 1) {
      fillFromValue(cleaned, index);
      return;
    }

    const nextChars = [...codeChars];
    nextChars[index] = { ...nextChars[index], char: cleaned };
    setCharsWithFocus(nextChars, index + 1);
  };

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Backspace' && !codeChars[index].char && index > 0) {
      const nextChars = [...codeChars];
      nextChars[index - 1] = { ...nextChars[index - 1], char: '' };
      setCharsWithFocus(nextChars, index - 1);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    fillFromValue(event.clipboardData.getData('text'));
  };

  const fillFromValue = (rawValue: string, startIndex = 0) => {
    const cleaned = cleanupOtpCode(rawValue).slice(0, OTP_LENGTH);
    if (!cleaned) {
      return;
    }

    const nextChars = [...codeChars];
    let targetIndex = startIndex;

    for (let i = 0; i < cleaned.length && targetIndex < OTP_LENGTH; i += 1) {
      nextChars[targetIndex] = {
        ...nextChars[targetIndex],
        char: cleaned[i]
      };
      targetIndex += 1;
    }

    setCharsWithFocus(
      nextChars,
      targetIndex >= OTP_LENGTH ? OTP_LENGTH - 1 : targetIndex
    );
  };

  const setCharsWithFocus = (chars: OtpCharEntry[], focusIndex: number) => {
    setCodeChars(chars);
    inputRefs.current[
      Math.max(0, Math.min(OTP_LENGTH - 1, focusIndex))
    ]?.focus();
  };

  useEffect(() => {
    const code = codeChars.map(({ char }) => char).join('');
    const isComplete = codeChars.every(({ char }) => char !== '');

    onCompletionChanged?.(code, isComplete);
  }, [codeChars, onCompletionChanged]);

  return (
    <Box
      display="flex"
      justifyContent="center"
      gap={1}
      my={4}
      onPaste={handlePaste}
    >
      {codeChars.map(({ id, char }, index) => (
        <input
          key={id}
          ref={ref => {
            inputRefs.current[index] = ref;
          }}
          value={char}
          onChange={event => handleChange(index, event.target.value)}
          onKeyDown={event => handleKeyDown(index, event)}
          type="text"
          autoComplete="one-time-code"
          maxLength={1}
          style={otpInputStyle}
          aria-label={`OTP digit ${index + 1}`}
        />
      ))}
    </Box>
  );
};

export default OtpVerificationField;
